interface SkeletonProps {
  className?: string;
}

export function Skeleton({ className = '' }: SkeletonProps) {
  return (
    <div
      className={`skeleton-contrail rounded ${className}`}
    />
  );
}

/** Contrail-style loading bar — a thin animated sweep for inline loading states. */
export function ContrailLoader({ className = '' }: { className?: string }) {
  return <div className={`contrail-loading w-full ${className}`} />;
}

export function BookingCardSkeleton() {
  return (
    <div className="p-4 bg-white dark:bg-stone-900 rounded-lg border border-stone-200 dark:border-stone-700">
      <div className="flex items-start justify-between gap-4">
        {/* Left: Flight info */}
        <div className="flex-1 min-w-0">
          {/* PNR + badges */}
          <div className="flex items-center gap-2 mb-2">
            <Skeleton className="h-4 w-16" />
            <Skeleton className="h-4 w-8" />
          </div>

          {/* Route */}
          <Skeleton className="h-7 w-40 mb-1.5" />

          {/* Date */}
          <div className="flex items-center gap-2">
            <Skeleton className="h-4 w-28" />
          </div>

          {/* Status badge */}
          <div className="mt-2">
            <Skeleton className="h-6 w-32 rounded-full" />
          </div>
        </div>

        {/* Right: Price */}
        <div className="text-right">
          <Skeleton className="h-3 w-16 mb-1" />
          <Skeleton className="h-8 w-20 mb-1" />
          <Skeleton className="h-3 w-10" />
        </div>
      </div>
    </div>
  );
}

export function StatCardSkeleton() {
  return (
    <div className="p-6 bg-white dark:bg-stone-900 rounded-lg border border-stone-200 dark:border-stone-700">
      <Skeleton className="h-3 w-24 mb-2" />
      <Skeleton className="h-8 w-16 mb-1" />
      <Skeleton className="h-3 w-20" />
    </div>
  );
}

export function HeroStatSkeleton() {
  return (
    <div className="p-6 bg-stone-100 dark:bg-stone-800 rounded-lg">
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <Skeleton className="h-4 w-32 mb-2" />
          <Skeleton className="h-12 w-24 mb-2" />
          <Skeleton className="h-4 w-40" />
        </div>
        <Skeleton className="h-16 w-16 rounded-lg opacity-20" />
      </div>
    </div>
  );
}

export function BookingsListSkeleton({ count = 3 }: { count?: number }) {
  return (
    <div className="space-y-3">
      {Array.from({ length: count }).map((_, i) => (
        <BookingCardSkeleton key={i} />
      ))}
    </div>
  );
}

export function DashboardSkeleton() {
  return (
    <div className="space-y-4">
      {/* Hero Stats Skeleton */}
      <HeroStatSkeleton />

      {/* Secondary Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCardSkeleton />
        <StatCardSkeleton />
        <StatCardSkeleton />
        <StatCardSkeleton />
      </div>

      {/* Filter Chips Skeleton */}
      <div className="flex flex-wrap gap-2">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-9 w-16 rounded-lg" />
        ))}
      </div>

      {/* Bookings List Skeleton */}
      <div className="p-6 bg-white dark:bg-stone-900 rounded-lg border border-stone-200 dark:border-stone-700">
        <div className="flex items-center justify-between mb-3">
          <Skeleton className="h-6 w-32" />
          <Skeleton className="h-6 w-6 rounded" />
        </div>
        <BookingsListSkeleton count={4} />
      </div>
    </div>
  );
}
